import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Maximize, Wifi, Wind, Tv, Bath, Calendar, Loader2, Check } from 'lucide-react';
import { formatPrice, formatDate } from './pricing';
import { PriceBreakdown, useTerms, termsLines } from './PriceBreakdown';
import { RateOffers } from './RateOffers';
import { useLanguage } from '../../context/LanguageContext';

const SEASON_HINT = {
  peak: '🔥 Peak season rates apply (+50%)',
  high: '☀️ High season rates apply (+25%)',
  low: '❄️ Low season rates apply (-15%)',
  shoulder: '🌿 Shoulder season rates',
};

// Sticky booking sidebar on the /room/:id page. Purely presentational — parent
// owns booking + pricing state and handlers.
export const BookingSidebar = ({ room, pricing, bookingData, loading, onBook }) => {
  const amenityItems = (room.amenities || []).map((a, i) => (
    <div key={i} className="flex items-center gap-2 text-gray-600">
      <Wifi size={18} className="text-orange-500" />
      <span className="text-sm">{a}</span>
    </div>
  ));

  const breakdownRows = (pricing?.pricing?.daily_breakdown || []).map((day, i) => (
    <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-600">
        {formatDate(day.date)}
        {day.is_weekend && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Weekend</span>}
      </span>
      <span className="font-semibold text-gray-900">{formatPrice(day.price)}</span>
    </div>
  ));

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24" data-testid="booking-sidebar">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{room.name}</h1>

      {pricing ? (
        <div className="mb-6">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-4xl font-bold text-orange-500">{formatPrice(pricing.pricing.total_price)}</span>
            <span className="text-gray-500">total</span>
          </div>
          <p className="text-gray-600 text-sm">
            {formatPrice(pricing.pricing.price_per_night)}/night avg • {bookingData?.numNights} nights
          </p>
        </div>
      ) : (
        <div className="flex items-baseline gap-2 mb-6">
          <span className="text-4xl font-bold text-orange-500">€{room.price}</span>
          <span className="text-gray-500">/night</span>
          <span className="ml-1 text-[10px] font-medium uppercase tracking-wider text-gray-400">from</span>
        </div>
      )}

      <div className="flex items-center gap-6 mb-6 text-gray-600">
        <div className="flex items-center gap-2">
          <Maximize size={20} />
          <span className="font-medium">{room.size} m²</span>
        </div>
        <div className="flex items-center gap-2">
          <Users size={20} />
          <span className="font-medium">{room.guests} Guests</span>
        </div>
      </div>

      {pricing && bookingData && (
        <div className="mb-6 p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={18} className="text-orange-500" />
            <h3 className="font-semibold text-gray-900">Your Stay</h3>
          </div>
          <div className="text-sm text-gray-600 mb-3">
            <p className="font-medium">{formatDate(bookingData.checkIn)} → {formatDate(bookingData.checkOut)}</p>
            <p>{bookingData.adults} adult{bookingData.adults > 1 ? 's' : ''}{bookingData.children > 0 ? `, ${bookingData.children} child${bookingData.children > 1 ? 'ren' : ''}` : ''}</p>
          </div>
          <div className="border-t border-orange-200 pt-3">
            <h4 className="font-semibold text-gray-700 text-sm mb-2">Price Breakdown</h4>
            {breakdownRows}
          </div>
          <div className="border-t border-orange-200 mt-3 pt-3 flex justify-between items-center">
            <span className="font-bold text-gray-900">Total</span>
            <span className="text-2xl font-bold text-orange-600">{formatPrice(pricing.pricing.total_price)}</span>
          </div>
          {pricing.pricing.daily_breakdown?.[0]?.season && SEASON_HINT[pricing.pricing.daily_breakdown[0].season] && (
            <div className="mt-3 text-xs text-gray-500 text-center">
              {SEASON_HINT[pricing.pricing.daily_breakdown[0].season]}
            </div>
          )}
        </div>
      )}

      {pricing && bookingData && <PriceBreakdown pricing={pricing} bookingData={bookingData} />}
      {pricing && bookingData && <RateOffers pricing={pricing} bookingData={bookingData} onBook={(voucher) => onBook(voucher)} />}

      {!bookingData && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-gray-600 text-sm text-center">
            <Link to="/#rooms" className="text-orange-500 font-medium hover:underline">Select dates</Link> to see pricing breakdown
          </p>
        </div>
      )}

      {loading && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl flex items-center justify-center gap-2">
          <Loader2 size={20} className="animate-spin text-orange-500" />
          <span className="text-gray-600">Loading prices...</span>
        </div>
      )}

      <p className="text-gray-600 mb-6 leading-relaxed">{room.description}</p>

      <div className="mb-6">
        <h2 className="font-semibold text-gray-900 mb-3">Room Amenities</h2>
        <div className="grid grid-cols-2 gap-3">{amenityItems}</div>
      </div>

      <div className="mb-6 pt-4 border-t">
        <h2 className="font-semibold text-gray-900 mb-3">Standard Features</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-gray-600"><Wifi size={18} className="text-orange-500" /><span className="text-sm">Free WiFi</span></div>
          <div className="flex items-center gap-2 text-gray-600"><Wind size={18} className="text-orange-500" /><span className="text-sm">Air Conditioning</span></div>
          <div className="flex items-center gap-2 text-gray-600"><Tv size={18} className="text-orange-500" /><span className="text-sm">Flat-screen TV</span></div>
          <div className="flex items-center gap-2 text-gray-600"><Bath size={18} className="text-orange-500" /><span className="text-sm">Private Bathroom</span></div>
        </div>
      </div>

      {pricing && bookingData ? (
        <button
          onClick={onBook}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          data-testid="room-detail-book-btn"
        >
          <Check size={20} />
          Finalize Your Reservation
        </button>
      ) : (
        <Link
          to="/#rooms"
          className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl text-center"
          data-testid="room-detail-book-btn"
        >
          Check Availability & Book
        </Link>
      )}

      <TermsFootnote />
    </div>
  );
};

// Replaces the old hard-coded "Secure booking • Free cancellation" with the configured terms.
const TermsFootnote = () => {
  const { lang } = useLanguage();
  const terms = useTerms();
  if (!terms) return null;
  const { pay, cancel } = termsLines(lang, terms);
  return <p className="text-center text-gray-500 text-xs mt-3 leading-relaxed" data-testid="booking-terms-footnote">{pay}{cancel ? ` ${cancel}` : ''}</p>;
};
